import React, { useEffect, useCallback, useMemo, createContext, useContext } from "react";
import ReactDOM from "react-dom";
import { FiX } from "react-icons/fi";
import "./Drawer.scss";

const DrawerContext = createContext({ activeKey: null, onSelect: () => {} });

// Pila global de drawers abiertos para que Esc sólo cierre el más reciente
// (útil cuando un drawer abre otro drawer encima, p.ej. Contratos → ficha de empleado).
const drawerStack = [];

const WIDTH_PRESETS = {
	sm: 480,
	md: 640,
	lg: 800,
	xl: 960,
};

const resolveWidth = (width) => {
	if (width == null) return 720;
	if (typeof width === "number") return width;
	if (typeof width === "string") {
		if (WIDTH_PRESETS[width]) return WIDTH_PRESETS[width];
		return width;
	}
	return 720;
};

/**
 * Drawer reutilizable que se desliza desde la derecha.
 *
 * Props:
 *  - isOpen: boolean (requerido)
 *  - onClose: () => void (requerido)
 *  - width: number | "sm" | "md" | "lg" | "xl" | string (default 720)
 *  - closeOnOverlayClick: boolean (default true)
 *  - closeOnEsc: boolean (default true)
 *  - shouldConfirmClose: boolean — si true, se pide confirmación antes de cerrar
 *  - confirmCloseMessage: string — mensaje para la confirmación
 *  - className, style: personalización del panel
 *  - children: contenido (normalmente Drawer.Sidebar + Drawer.Main)
 *
 * También acepta props activeKey/onSelect para el contexto de tabs, si se usan
 * Drawer.Nav / Drawer.NavItem / Drawer.TabPane de forma declarativa.
 */
const Drawer = ({
	isOpen,
	onClose,
	width = 720,
	closeOnOverlayClick = true,
	closeOnEsc = true,
	shouldConfirmClose = false,
	confirmCloseMessage = "Hay cambios sin guardar. ¿Deseas salir sin guardar?",
	className = "",
	style = {},
	activeKey = null,
	onSelect = null,
	children,
}) => {
	const requestClose = useCallback(() => {
		if (shouldConfirmClose) {
			// eslint-disable-next-line no-alert
			const ok = window.confirm(confirmCloseMessage);
			if (!ok) return;
		}
		if (typeof onClose === "function") onClose();
	}, [shouldConfirmClose, confirmCloseMessage, onClose]);

	useEffect(() => {
		if (!isOpen) return undefined;

		const token = { closeOnEsc, requestClose };
		drawerStack.push(token);

		const handleKeyDown = (e) => {
			if (e.key !== "Escape") return;
			const top = drawerStack[drawerStack.length - 1];
			if (top !== token) return;
			if (!top.closeOnEsc) return;
			e.stopPropagation();
			top.requestClose();
		};

		document.addEventListener("keydown", handleKeyDown);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		document.body.classList.add("drawer-open");

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			const idx = drawerStack.indexOf(token);
			if (idx !== -1) drawerStack.splice(idx, 1);
			if (drawerStack.length === 0) {
				document.body.style.overflow = prevOverflow;
				document.body.classList.remove("drawer-open");
			}
		};
	}, [isOpen, closeOnEsc, requestClose]);

	const handleBackdropClick = (e) => {
		// Los portales de React propagan eventos por el árbol de componentes (no por el DOM).
		// Sin esto, clics en el drawer pueden activar handlers de la vista de fondo (p. ej. planificación).
		e.stopPropagation();
		if (!closeOnOverlayClick) return;
		if (e.target === e.currentTarget) {
			requestClose();
		}
	};

	const stopEventBubble = (e) => {
		e.stopPropagation();
	};

	const ctxValue = useMemo(
		() => ({ activeKey, onSelect: onSelect || (() => {}), requestClose }),
		[activeKey, onSelect, requestClose]
	);

	if (!isOpen) return null;

	const resolvedWidth = resolveWidth(width);
	const panelStyle = {
		...style,
		width:
			typeof resolvedWidth === "number" ? `${resolvedWidth}px` : resolvedWidth,
	};

	const panel = (
		<div
			className="drawer-backdrop"
			onMouseDown={handleBackdropClick}
			onClick={stopEventBubble}
			onPointerDown={stopEventBubble}
		>
			<div
				className={`drawer-panel ${className}`}
				style={panelStyle}
				role="dialog"
				aria-modal="true"
				onMouseDown={stopEventBubble}
				onClick={stopEventBubble}
				onPointerDown={stopEventBubble}
			>
				<DrawerContext.Provider value={ctxValue}>
					{children}
				</DrawerContext.Provider>
			</div>
		</div>
	);

	return ReactDOM.createPortal(panel, document.body);
};

// --- Subcomponentes compositivos ---

/**
 * Drawer.Tabs — provee el contexto de tabs (activeKey/onSelect) a los
 * subcomponentes que cuelguen debajo (Drawer.Nav, Drawer.NavItem, Drawer.TabPane).
 *
 * Necesario cuando el estado de la tab activa vive en un componente interno
 * (hijo del Drawer) en lugar de pasarse como prop al Drawer padre.
 */
const Tabs = ({ activeKey = null, onSelect = null, children }) => {
	const parent = useContext(DrawerContext);
	const value = useMemo(
		() => ({
			...parent,
			activeKey,
			onSelect: onSelect || (() => {}),
		}),
		[parent, activeKey, onSelect]
	);
	return (
		<DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
	);
};

const Sidebar = ({ className = "", children, width, style = {} }) => (
	<aside
		className={`drawer-sidebar ${className}`}
		style={width ? { ...style, flexBasis: typeof width === "number" ? `${width}px` : width } : style}
	>
		{children}
	</aside>
);

const SidebarHeader = ({ className = "", title, subtitle, children }) => (
	<div className={`drawer-sidebar__header ${className}`}>
		{title && <h5 className="drawer-sidebar__title">{title}</h5>}
		<p className={`drawer-sidebar__subtitle ${subtitle ? "" : "is-empty"}`}>
			{subtitle || ""}
		</p>
		{children}
	</div>
);

const Nav = ({ className = "", activeKey: activeKeyProp, onSelect: onSelectProp, children }) => {
	const ctx = useContext(DrawerContext);
	const activeKey = activeKeyProp !== undefined ? activeKeyProp : ctx.activeKey;
	const onSelect = onSelectProp || ctx.onSelect;

	return (
		<nav className={`drawer-nav ${className}`}>
			<ul className="drawer-nav__list">
				{React.Children.map(children, (child) => {
					if (!React.isValidElement(child)) return child;
					return React.cloneElement(child, {
						__drawerActive:
							child.props.eventKey !== undefined &&
							child.props.eventKey === activeKey,
						__drawerOnSelect: onSelect,
					});
				})}
			</ul>
		</nav>
	);
};

const NavItem = ({
	eventKey,
	children,
	icon,
	disabled = false,
	className = "",
	__drawerActive = false,
	__drawerOnSelect,
	onClick,
}) => {
	const handleClick = (e) => {
		if (disabled) return;
		if (onClick) onClick(e);
		if (__drawerOnSelect) __drawerOnSelect(eventKey, e);
	};

	return (
		<li className={`drawer-nav__item ${className}`}>
			<button
				type="button"
				className={`drawer-nav__link ${__drawerActive ? "is-active" : ""} ${
					disabled ? "is-disabled" : ""
				}`}
				onClick={handleClick}
				disabled={disabled}
			>
				{icon && <span className="drawer-nav__icon">{icon}</span>}
				<span className="drawer-nav__label">{children}</span>
			</button>
		</li>
	);
};

const Main = ({ className = "", children }) => (
	<section className={`drawer-main ${className}`}>{children}</section>
);

const MainHeader = ({
	title,
	subtitle,
	actions,
	showClose = true,
	className = "",
	children,
}) => {
	const ctx = useContext(DrawerContext);

	return (
		<header className={`drawer-main__header ${className}`}>
			<div className="drawer-main__header-left">
				{actions && <div className="drawer-main__header-actions">{actions}</div>}
				{title && <h4 className="drawer-main__title">{title}</h4>}
				{subtitle && <p className="drawer-main__subtitle">{subtitle}</p>}
				{children}
			</div>
			{showClose && (
				<button
					type="button"
					className="drawer-main__close btn-transparent"
					aria-label="Cerrar"
					onClick={() => ctx.requestClose && ctx.requestClose()}
				>
					<FiX size={20} />
				</button>
			)}
		</header>
	);
};

const Body = ({ className = "", children, noPadding = false }) => (
	<div
		className={`drawer-body ${noPadding ? "drawer-body--no-padding" : ""} ${className}`}
	>
		{children}
	</div>
);

const TabPane = ({ eventKey, children, className = "", mountOnEnter = false, unmountOnExit = false }) => {
	const ctx = useContext(DrawerContext);
	const isActive = ctx.activeKey === eventKey;

	if (!isActive && unmountOnExit) return null;
	if (!isActive && !mountOnEnter) {
		return (
			<div className={`drawer-tab-pane ${className}`} hidden>
				{children}
			</div>
		);
	}
	if (!isActive) return null;

	return <div className={`drawer-tab-pane is-active ${className}`}>{children}</div>;
};

const Footer = ({ className = "", children, align = "right" }) => (
	<footer className={`drawer-footer drawer-footer--${align} ${className}`}>
		{children}
	</footer>
);

Drawer.Tabs = Tabs;
Drawer.Sidebar = Sidebar;
Drawer.SidebarHeader = SidebarHeader;
Drawer.Nav = Nav;
Drawer.NavItem = NavItem;
Drawer.Main = Main;
Drawer.MainHeader = MainHeader;
Drawer.Body = Body;
Drawer.TabPane = TabPane;
Drawer.Footer = Footer;

export default Drawer;
